/**
 * Routine TEMPLATE model — sections, step order, drag interpretation, weights.
 *
 * A routine template is two documents, not one: a `buckets/{id}` doc holding
 * `sections[]` and `taskIds[]` (the order of record), and one `tasks/{id}` per
 * step tagged `source: 'routineTemplate'` carrying `sectionId` and `points`.
 * Everything here is the pure logic that turns those two into a list, and a
 * rearranged list back into writes.
 *
 * It lives in a package because BOTH apps arrange routines now: the native
 * editor and the grid on `use.x9.app`. Two implementations of "what order are
 * the steps in" would diverge silently, and the first symptom would be a run
 * dispatched in the wrong order — days later, on somebody else's phone.
 *
 * Sibling of `@x9/routine-run-model`, which owns the run once it is dispatched.
 * Nothing here touches Firestore, React or React Native.
 *
 * See docs/meta/prd-web-routine-builder-2026-09.md (D2).
 */

/** Default weight for a new / cloned / converted template step. */
const DEFAULT_ROUTINE_POINTS = 1;

/** Inclusive points range for routine template tasks. */
const ROUTINE_POINTS_MIN = 1;
const ROUTINE_POINTS_MAX = 10;

/** Section title cap for add/rename, paste, and MD import. */
const SECTION_NAME_MAX = 160;

/** Clamp / coerce a points value into the legal 1–10 integer range. */
function clampRoutinePoints(value, fallback = DEFAULT_ROUTINE_POINTS) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(ROUTINE_POINTS_MAX, Math.max(ROUTINE_POINTS_MIN, Math.round(n)));
}

function clampSectionName(name) {
  const trimmed = typeof name === 'string' ? name.trim() : '';
  if (!trimmed) return '';
  return trimmed.length > SECTION_NAME_MAX ? trimmed.slice(0, SECTION_NAME_MAX) : trimmed;
}

/** Generate a stable routine section id. */
function generateRoutineSectionId() {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 10);
  return `sec_${timestamp}_${random}`;
}

/**
 * Build a normalized section from partial input.
 */
function createRoutineSection(partial = {}) {
  const name = clampSectionName(partial.name);
  return {
    sectionId: partial.sectionId || generateRoutineSectionId(),
    name: name || 'General',
    order: Number.isFinite(partial.order) ? partial.order : 0,
  };
}

/**
 * Normalize sections: trim names, drop empties after fallback, contiguous order.
 * Always returns at least one section when `ensureDefault` is true (default),
 * so "a routine with no sections" is not a state any editor has to render.
 */
function normalizeRoutineSections(sections, options = {}) {
  const ensureDefault = options.ensureDefault !== false;
  const defaultName =
    typeof options.defaultName === 'string' && options.defaultName.trim()
      ? options.defaultName.trim()
      : 'General';
  const list = Array.isArray(sections) ? sections : [];
  const normalized = list
    .map((s, index) => createRoutineSection({ ...s, order: index }))
    .filter((s) => s.sectionId);
  if (normalized.length === 0 && ensureDefault) {
    return [createRoutineSection({ name: defaultName, order: 0 })];
  }
  return normalized.map((s, index) => ({ ...s, order: index }));
}

/** First section id (default landing place for new template tasks). */
function getDefaultSectionId(bucketOrSections) {
  const sections = Array.isArray(bucketOrSections)
    ? bucketOrSections
    : normalizeRoutineSections(bucketOrSections?.sections, { ensureDefault: true });
  return sections[0]?.sectionId || null;
}

/** Σ points for tasks belonging to a section. */
function sumSectionPoints(tasks, sectionId) {
  if (!sectionId || !Array.isArray(tasks)) return 0;
  let total = 0;
  for (const task of tasks) {
    if (task?.sectionId !== sectionId) continue;
    total += clampRoutinePoints(task.points);
  }
  return total;
}

/**
 * Sort template tasks the way the routine reads: by section order, then by
 * position in `taskIds`, then by creation time for anything filed under the
 * bucket but missing from `taskIds` — shown last rather than dropped, because
 * a step that vanishes is worse than one out of place.
 */
function sortTemplateTasksBySection(tasks, sections, taskIds) {
  const sectionOrder = new Map(
    normalizeRoutineSections(sections, { ensureDefault: true }).map((s, i) => [s.sectionId, i]),
  );
  const idOrder = new Map((taskIds || []).map((id, i) => [id, i]));
  return [...(tasks || [])].sort((a, b) => {
    const sa = sectionOrder.has(a.sectionId) ? sectionOrder.get(a.sectionId) : Number.MAX_SAFE_INTEGER;
    const sb = sectionOrder.has(b.sectionId) ? sectionOrder.get(b.sectionId) : Number.MAX_SAFE_INTEGER;
    if (sa !== sb) return sa - sb;
    const ia = idOrder.has(a.id) ? idOrder.get(a.id) : Number.MAX_SAFE_INTEGER;
    const ib = idOrder.has(b.id) ? idOrder.get(b.id) : Number.MAX_SAFE_INTEGER;
    if (ia !== ib) return ia - ib;
    return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
  });
}

/**
 * Interpret a mixed section-header + task row list after a move.
 *
 * Walking top→bottom: each task inherits the nearest preceding header's
 * sectionId. This is what lets one gesture both reorder and re-section, and
 * why the grid and the phone's drag list can share a single model.
 *
 * @param {Array<{type:'header'|'task', section?: {sectionId}, task?: {id, sectionId}}>} rows
 * @returns {{
 *   sectionOrder: string[],
 *   taskIds: string[],
 *   taskAssignments: Array<{ taskId: string, sectionId: string }>,
 * }}
 */
function applyRoutineListDrag(rows) {
  const sectionOrder = [];
  const taskIds = [];
  const taskAssignments = [];
  let currentSectionId = null;

  for (const row of Array.isArray(rows) ? rows : []) {
    if (!row) continue;
    if (row.type === 'header' && row.section?.sectionId) {
      currentSectionId = row.section.sectionId;
      if (!sectionOrder.includes(currentSectionId)) {
        sectionOrder.push(currentSectionId);
      }
      continue;
    }
    if (row.type === 'task' && row.task?.id) {
      taskIds.push(row.task.id);
      if (currentSectionId) {
        taskAssignments.push({ taskId: row.task.id, sectionId: currentSectionId });
      }
    }
  }

  return { sectionOrder, taskIds, taskAssignments };
}

/**
 * Rebuild `taskIds` after a section-only reorder: walk sections in the new
 * order and append each section's prior members in their previous relative order.
 */
function rebuildTaskIdsForSectionOrder(sectionOrder, previousTaskIds, tasksById) {
  const seen = new Set();
  const next = [];
  const order = Array.isArray(sectionOrder) ? sectionOrder : [];
  const prev = Array.isArray(previousTaskIds) ? previousTaskIds : [];

  for (const sectionId of order) {
    for (const id of prev) {
      if (seen.has(id)) continue;
      const task = tasksById?.get?.(id);
      if (task?.sectionId === sectionId) {
        next.push(id);
        seen.add(id);
      }
    }
  }
  // Preserve any remaining ids (unknown section) at the end.
  for (const id of prev) {
    if (!seen.has(id)) next.push(id);
  }
  return next;
}

/**
 * Next template version. Any structural edit bumps this so runs can record the
 * exact template version they were born from (isolation guarantee).
 */
function nextTemplateVersion(currentVersion) {
  const n = Number(currentVersion);
  return Number.isFinite(n) && n >= 0 ? n + 1 : 1;
}

/**
 * Flatten sections + sorted tasks into the row list both editors render and
 * `applyRoutineListDrag` reads back. A section with no steps still gets its
 * header, so an empty section is somewhere you can drop a step rather than a
 * thing that silently disappeared.
 */
function buildRoutineRows(sections, tasks) {
  const normalized = normalizeRoutineSections(sections, { ensureDefault: true });
  const rows = [];
  const placed = new Set();

  for (const section of normalized) {
    rows.push({ type: 'header', section });
    for (const task of tasks || []) {
      if (task?.sectionId !== section.sectionId) continue;
      rows.push({ type: 'task', task });
      placed.add(task.id);
    }
  }

  // Steps whose section no longer exists: keep them visible under the last
  // header rather than losing them to a deleted section.
  for (const task of tasks || []) {
    if (task?.id && !placed.has(task.id)) rows.push({ type: 'task', task });
  }

  return rows;
}

/** The rows a move carries: a step alone, a section header with its steps. */
function blockAt(rows, index) {
  if (rows[index]?.type !== 'header') return { start: index, end: index + 1 };
  let end = index + 1;
  while (end < rows.length && rows[end].type === 'task') end += 1;
  return { start: index, end };
}

/**
 * Move a row (with its block) to sit before position `to` in the current list.
 *
 * One function behind both the drag-and-drop drop handler and the keyboard
 * nudge, so a step dragged one place and a step moved with Alt+↓ produce
 * byte-identical writes — otherwise the two would drift and only one of them
 * would be covered by tests.
 *
 * Headers move with their steps: dragging a title alone would leave its steps
 * behind, which reads as the section having been emptied.
 */
function moveRoutineRowTo(rows, from, to) {
  const list = Array.isArray(rows) ? [...rows] : [];
  if (!list[from]) return list;

  const { start, end } = blockAt(list, from);
  // Dropping inside the block being dragged is a no-op, not a scramble.
  if (to > start && to < end) return list;

  const block = list.slice(start, end);
  const rest = [...list.slice(0, start), ...list.slice(end)];
  const target = to > start ? to - block.length : to;
  rest.splice(Math.max(0, Math.min(rest.length, target)), 0, ...block);
  return rest;
}

/**
 * Nudge a row one position. A step steps over one row at a time — including
 * over a header, which is how it changes section; a header jumps the whole
 * neighbouring block, so sections swap rather than interleave.
 */
function moveRoutineRow(rows, index, direction) {
  const list = Array.isArray(rows) ? [...rows] : [];
  const row = list[index];
  if (!row) return list;

  if (row.type === 'task') {
    const target = direction === 'up' ? index - 1 : index + 2;
    if (target < 0 || target > list.length) return list;
    return moveRoutineRowTo(list, index, target);
  }

  const { start, end } = blockAt(list, index);
  if (direction === 'up') {
    if (start === 0) return list;
    const previous = blockAt(list, blockStartBefore(list, start));
    return moveRoutineRowTo(list, index, previous.start);
  }
  if (end >= list.length) return list;
  const next = blockAt(list, end);
  return moveRoutineRowTo(list, index, next.end);
}

/** Walk back to the header that opens the block ending at `index`. */
function blockStartBefore(rows, index) {
  let start = index - 1;
  while (start > 0 && rows[start].type === 'task') start -= 1;
  return start;
}

module.exports = {
  DEFAULT_ROUTINE_POINTS,
  ROUTINE_POINTS_MIN,
  ROUTINE_POINTS_MAX,
  SECTION_NAME_MAX,
  clampRoutinePoints,
  clampSectionName,
  generateRoutineSectionId,
  createRoutineSection,
  normalizeRoutineSections,
  getDefaultSectionId,
  sumSectionPoints,
  sortTemplateTasksBySection,
  applyRoutineListDrag,
  rebuildTaskIdsForSectionOrder,
  nextTemplateVersion,
  buildRoutineRows,
  moveRoutineRow,
  moveRoutineRowTo,
};
